import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_CONTENT_FILTERING_RULES_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { toggleVisibility } from '../../../../../../../../../../utils/htmlElement.utils';
import { ExchangeContentControlFilteringRuleFormValue } from '../../../../../../../utils/uiSettings/exchange/exchange-protection.types';
import {
  getExchangeApplyToRuleFormOptions,
  getExchangeContentControlActionRuleFormOptions,
} from '../../../../../../../utils/uiSettings/exchange/exchange-protection.values';
import {
  ExchangeRuleScopeApplyToType,
  ExchangeRuleScopeEmailSelection,
  ExchangeContentControlContentFilteringRuleSettingsFilterByExpression,
  ExchangeContentControlRuleActionType,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../../../../ui/gzRadio';
import { GzExchangeUserGroupsGridWebComponent } from '../../../../../helpers/gridComponents/userGroupsWithSelection/GzExchangeUserGroupsGridWebComponent';
import { CustomizableValidator } from '../../../../../../../../../../ui/gzForm/validators/customizable.validator';
import {
  ruleFilteringValidators,
  ruleScopeSpecificValidators,
  validateSettingFilterSelectionGroup,
} from '../utils/validators';
import _ from 'lodash';
import { GzExchangeContentControlContentFilteringByGridWebComponent } from '../gridComponents/GzExchangeContentControlContentFilteringByGridWebComponent';
import { FontSizes, FontWeights } from '../../../../../../../../../../types/common/types';
import { validateHeaderName, validateMultipleEmails, validatePathWin } from '../../../../../helpers/utils/validators';
import { EmailValidator } from '../../../../../../../../../../ui/gzForm/validators/email.validator';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_CONTENT_FILTERING_RULES_FORM)
export class GzExchangeContentControlContentFilteringRulesFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ExchangeContentControlFilteringRuleFormValue> {
  // general section
  @query('#name')
  private ruleName: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#priority')
  private rulePriority: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#active-form-field')
  private activeFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // scope section
  @query('#rule-scope-apply-to-field')
  private ruleScopeApplyToField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-scope-from-field')
  private ruleScopeFromField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-from-specific-field')
  private ruleScopeFromSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-to-field')
  private ruleScopeToField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-to-specific-field')
  private ruleScopeToSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-match-all-recipients-field')
  private ruleScopeMatchAllRecipientsField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // settings section
  @query('#rule-settings-filter-by-subject-enabled-field')
  private ruleSettingsFilterBySubjectEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-filter-by-subject-expressions-field')
  private ruleSettingsFilterBySubjectExpressionsField: GzFormFieldWebComponent<
    GzExchangeContentControlContentFilteringByGridWebComponent,
    ExchangeContentControlContentFilteringRuleSettingsFilterByExpression[]
  >;
  @query('#rule-settings-filter-by-body-content-enabled-field')
  private ruleSettingsFilterByBodyContentEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-filter-by-body-content-expressions-field')
  private ruleSettingsFilterByBodyContentExpressionsField: GzFormFieldWebComponent<
    GzExchangeContentControlContentFilteringByGridWebComponent,
    ExchangeContentControlContentFilteringRuleSettingsFilterByExpression[]
  >;

  // action section
  @query('#rule-actions-type-field')
  private ruleActionsTypeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-deliver-to-field')
  private ruleActionsDeliverToField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-modify-subject-field')
  private ruleActionsModifySubjectField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-modify-subject-text-field')
  private ruleActionsModifySubjectTextField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-append-header-field')
  private ruleActionsAppendHeaderField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-append-header-name-field')
  private ruleActionsAppendHeaderNameField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-append-header-value-field')
  private ruleActionsAppendHeaderValueField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-save-mail-to-disk-field')
  private ruleActionsSaveMailToDiskField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-save-mail-to-disk-path-field')
  private ruleActionsSaveMailToDiskPathField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-archive-to-account-field')
  private ruleActionsArchiveToAccountField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-archive-to-account-address-field')
  private ruleActionsArchiveToAccountAddressField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rules-actions-notify-section')
  private ruleActionsNotifySection: HTMLElement;
  @query('#rule-actions-notify-recipient-field')
  private ruleActionsNotifyRecipientField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-sender-field')
  private ruleActionsNotifySenderField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-account-field')
  private ruleActionsNotifyAccountField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-account-recipients-field')
  private ruleActionsNotifyAccountRecipientsField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-stop-if-matched-field')
  private ruleActionsStopIfMatchedField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  public constructor(private readonly maxPriority: number, value?: ExchangeContentControlFilteringRuleFormValue) {
    super(value);
    this.maxPriority = maxPriority;
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.ruleName.setError(error);
  }

  protected getConvertedFormValue() {
    const value = _.cloneDeep(this.formGroup.value);

    // set modifiy subject (by default false if missing)
    const modifySubject = _.get(this.formGroup.value, ['actions', 'modifySubject'], false);
    _.set(value, ['actions', 'modifySubject'], modifySubject);

    // set archive to account (by default false if missing)
    const archiveToAccount = _.get(this.formGroup.value, ['actions', 'archiveToAccount'], false);
    _.set(value, ['actions', 'archiveToAccount'], archiveToAccount);

    // set append header (by default false if missing)
    const appendHeader = _.get(this.formGroup.value, ['actions', 'appendHeader'], false);
    _.set(value, ['actions', 'appendHeader'], appendHeader);

    return value;
  }

  protected createFormGroup(): void {
    this.setValidators();

    this.formGroup = FormGroup.build({
      defaultPositionInGrid: this.rulePriority,
      name: this.ruleName,
      active: this.activeFormField,
      scope: {
        applyTo: this.ruleScopeApplyToField,
        from: this.ruleScopeFromField,
        to: this.ruleScopeToField,
        fromSpecific: this.ruleScopeFromSpecificField,
        toSpecific: this.ruleScopeToSpecificField,
        matchAllRecipients: this.ruleScopeMatchAllRecipientsField,
      },
      settings: {
        filterBySubject: {
          enabled: this.ruleSettingsFilterBySubjectEnabledField,
          expressions: this.ruleSettingsFilterBySubjectExpressionsField,
        },
        filterByBodyContent: {
          enabled: this.ruleSettingsFilterByBodyContentEnabledField,
          expressions: this.ruleSettingsFilterByBodyContentExpressionsField,
        },
      },
      actions: {
        type: this.ruleActionsTypeField,
        deliverTo: this.ruleActionsDeliverToField,
        modifySubject: this.ruleActionsModifySubjectField,
        modifySubjectText: this.ruleActionsModifySubjectTextField,
        appendHeader: this.ruleActionsAppendHeaderField,
        headerName: this.ruleActionsAppendHeaderNameField,
        headerValue: this.ruleActionsAppendHeaderValueField,
        saveMailToDisk: this.ruleActionsSaveMailToDiskField,
        saveMailToDiskPath: this.ruleActionsSaveMailToDiskPathField,
        archiveToAccount: this.ruleActionsArchiveToAccountField,
        archiveToAccountAddress: this.ruleActionsArchiveToAccountAddressField,
        stopIfMatched: this.ruleActionsStopIfMatchedField,
        notifyRecipient: this.ruleActionsNotifyRecipientField,
        notifySender: this.ruleActionsNotifySenderField,
        notifyAccount: this.ruleActionsNotifyAccountField,
        notifyAccountRecipients: this.ruleActionsNotifyAccountRecipientsField,
      },
    });
  }

  protected setFormGroupValue() {
    if (!this.value) {
      this.ruleActionsTypeField.value = ExchangeContentControlRuleActionType.DELIVER;
      this.rulePriority.value = 1; // default will be added to the top
      return;
    }

    // for the first change, values from backend are used for secondary actions
    this.ruleActionsTypeField.setAttribute('no-impact', '');

    this.formGroup.value = {
      defaultPositionInGrid: this.value.defaultPositionInGrid,
      name: this.value.name,
      active: this.value.active,
      scope: this.value.scope,
      settings: this.value.settings,
      actions: this.value.actions,
    };
  }

  private setValidators() {
    this.ruleName.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleActionsModifySubjectTextField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleActionsDeliverToField.Validators = [
      RequiredValidator(translateFn('Required field')),
      EmailValidator(translateFn('Invalid email address')),
    ];
    this.ruleActionsAppendHeaderNameField.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => validateHeaderName(value)),
    ];
    this.ruleActionsAppendHeaderValueField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleActionsSaveMailToDiskPathField.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => validatePathWin(value)),
    ];
    this.ruleActionsArchiveToAccountAddressField.Validators = [
      RequiredValidator(translateFn('Required field')),
      EmailValidator(translateFn('Invalid email address')),
    ];
    this.ruleActionsNotifyAccountRecipientsField.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => validateMultipleEmails(value)),
    ];

    // set validators for target scan object group
    const settingFilterSelectionGroupValidator = [
      CustomizableValidator(() =>
        validateSettingFilterSelectionGroup([
          this.ruleSettingsFilterBySubjectEnabledField.value as boolean,
          this.ruleSettingsFilterByBodyContentEnabledField.value as boolean,
        ])
      ),
    ];
    this.ruleSettingsFilterBySubjectEnabledField.Validators = settingFilterSelectionGroupValidator;
    this.ruleSettingsFilterByBodyContentEnabledField.Validators = settingFilterSelectionGroupValidator;

    this.ruleScopeToSpecificField.touched = true;
    this.ruleScopeFromSpecificField.touched = true;
    this.ruleSettingsFilterBySubjectExpressionsField.touched = true;
    this.ruleSettingsFilterByBodyContentExpressionsField.touched = true;
    this.ruleSettingsFilterBySubjectEnabledField.touched = true;
    this.ruleSettingsFilterByBodyContentEnabledField.touched = true;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <!-- General section -->
        <div id="rules-general-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle">${translateFn('General')}</gz-title>
          <gz-form-field id="name" class="label-mw-250" label="${translateFn('Rule name')}" required>
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the rule name')}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="priority" class="label-mw-250 mt-2" label="${translateFn('Set priority')}" required>
            <gz-input-number
              gz-form-input
              inputWidth="${InputWidthValues.SMALL}"
              min="${1}"
              max="${this.maxPriority}"
            ></gz-input-number>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="active-form-field" class="mt-2">
            <gz-checkbox gz-form-input label="${translateFn('Active')}" checked></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule scope section -->
        <div id="rules-scope-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Rule scope')}</gz-title>
          <gz-form-field id="rule-scope-apply-to-field" label="${translateFn('Apply to (direction)')}">
            <gz-dropdown
              gz-form-input
              .options=${getExchangeApplyToRuleFormOptions()}
              .value=${ExchangeRuleScopeApplyToType.ALL}
            ></gz-dropdown>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Senders')}"
          >
            <gz-form-field id="rule-scope-from-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.ANY}
                @radioSelectionChanged=${this.handleScopeFromEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-from-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all senders. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-from-specific-field" class="hidden">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Recipients')}"
            class="mt-2"
          >
            <gz-form-field id="rule-scope-to-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.ANY}
                @radioSelectionChanged=${this.handleScopeToEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-to-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all recipients. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-to-specific-field" class="mt-2 hidden">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="rule-scope-match-all-recipients-field" class="hidden pb-2">
            <gz-checkbox label="${translateFn('Match all recipients')}" gz-form-input></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule settings section -->
        <div id="rules-settings-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-2">${translateFn('Settings')}</gz-title>
          <gz-text
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'Filter emails based on keywords, phrases or regular expressions detected in their subject or body. To check these elements, select both {bold}Filter by subject{/bold} and {bold}Filter by body content{/bold} options.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-settings-filter-by-subject-enabled-field" class="mt-2">
            <gz-checkbox
              label="${translateFn('Filter by subject')}"
              gz-form-input
              @change="${this.onSettingFilterBySubjectSelectionChanged}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="rule-settings-filter-by-subject-expressions-field" class="hidden">
            <gz-policy-panel-exchange-content-control-content-filtering-by-grid
              gz-form-input
            ></gz-policy-panel-exchange-content-control-content-filtering-by-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="rule-settings-filter-by-body-content-enabled-field">
            <gz-checkbox
              label="${translateFn('Filter by body content')}"
              gz-form-input
              @change="${this.onSettingFilterByBodyContentSelectionChanged}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="rule-settings-filter-by-body-content-expressions-field" class="hidden">
            <gz-policy-panel-exchange-content-control-content-filtering-by-grid
              gz-form-input
            ></gz-policy-panel-exchange-content-control-content-filtering-by-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>

        <!-- Rule actions section -->
        <div id="rules-actions-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Actions')}</gz-title>

          <gz-title fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.HEADING_6}" class="action-title mt-3"
            >${translateFn('Primary action')}</gz-title
          >

          <!-- Rule actions section: Primary action -->
          <div id="rule-actions-primary-section">
            <gz-form-field id="rule-actions-type-field">
              <gz-dropdown
                gz-form-input
                label="${translateFn('Action')}"
                .options=${getExchangeContentControlActionRuleFormOptions()}
                @optionSelected="${this.handleActionsTypeFieldChange}"
              ></gz-dropdown>
            </gz-form-field>

            <gz-form-field id="rule-actions-deliver-to-field" class="hidden" disabled>
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.LARGE}"
                placeholder="${translateFn('Enter the mail address')}"
              ></gz-input>
            </gz-form-field>
          </div>

          <div id="rule-actions-secondary-section">
            <gz-title fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.HEADING_6}"
              >${translateFn('Secondary actions')}</gz-title
            >

            <!-- Rule actions section: Tag the email subject as -->
            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-220" id="rule-actions-modify-subject-field">
                <gz-checkbox
                  label="${translateFn('Tag the email subject as')}"
                  gz-form-input
                  @change="${e => this.toggleDisabledField(this.ruleActionsModifySubjectTextField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-modify-subject-text-field" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter a label')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Email header -->
            <div>
              <gz-form-field class="checkbox-width-220" id="rule-actions-append-header-field">
                <gz-checkbox
                  label="${translateFn('Add an email header')}"
                  gz-form-input
                  @change="${e => this.toggleActionsAppendHeaderFieldDisabledState(!e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field
                class="hidden"
                id="rule-actions-append-header-name-field"
                label="${translateFn('Header name')}"
                disabled
              >
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter a name')}"
                ></gz-input>
              </gz-form-field>

              <gz-form-field
                class="hidden"
                id="rule-actions-append-header-value-field"
                label="${translateFn('Header value')}"
                disabled
              >
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter a value')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Save email to disk -->
            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-220" id="rule-actions-save-mail-to-disk-field">
                <gz-checkbox
                  label="${translateFn('Save email to disk')}"
                  gz-form-input
                  @change="${e => this.toggleDisabledField(this.ruleActionsSaveMailToDiskPathField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-save-mail-to-disk-path-field" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter the full path')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Archive to account -->
            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-220" id="rule-actions-archive-to-account-field">
                <gz-checkbox
                  label="${translateFn('Archive to account')}"
                  gz-form-input
                  @change="${e =>
                    this.toggleDisabledField(this.ruleActionsArchiveToAccountAddressField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-archive-to-account-address-field" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter the email archive address')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Notify section -->
            <div id="rules-actions-notify-section" class="hidden">
              <gz-form-field id="rule-actions-notify-recipient-field" disabled>
                <gz-checkbox label="${translateFn('Notify the recipient')}" gz-form-input></gz-checkbox>
              </gz-form-field>
              <gz-text
                id="rule-actions-notify-recipient-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText helper-text"
                >${translateFn(
                  'Send a notification to the intended recipients when an action is taken on an email. Emails are sent only to mailboxes on domains accepted by the company email server.'
                )}
              </gz-text>

              <gz-form-field id="rule-actions-notify-sender-field" disabled>
                <gz-checkbox label="${translateFn('Notify the sender')}" gz-form-input></gz-checkbox>
              </gz-form-field>
              <gz-text
                id="rule-actions-notify-sender-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText helper-text"
                >${translateFn(
                  'Send notification to the sender when an action is taken on an email. Emails are only sent to mailboxes that belong to a domain accepted by the company email server.'
                )}
              </gz-text>

              <div class="checkbox-with-input">
                <gz-form-field class="checkbox-width-220" id="rule-actions-notify-account-field" disabled>
                  <gz-checkbox
                    label="${translateFn('Notify the user')}"
                    gz-form-input
                    @change="${e =>
                      this.toggleDisabledField(this.ruleActionsNotifyAccountRecipientsField, !e.detail.checked)}"
                  ></gz-checkbox>
                </gz-form-field>

                <gz-form-field id="rule-actions-notify-account-recipients-field" disabled>
                  <gz-input
                    gz-form-input
                    inputWidth="${InputWidthValues.LARGE}"
                    placeholder="${translateFn('Enter the email address')}"
                  ></gz-input>
                </gz-form-field>
              </div>
              <gz-text
                id="rule-actions-notify-account-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText helper-text"
                >${translateFn('Send notification to the specified mailboxes when an action is taken on an email.')}
              </gz-text>
            </div>
          </div>

          <gz-form-field id="rule-actions-stop-if-matched-field">
            <gz-checkbox label="${translateFn('Stop processing more rules')}" gz-form-input checked></gz-checkbox>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  /**
   * Handles the change event of the scope from email selection field.
   * @param event
   * @private
   */
  private handleScopeFromEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeFromSpecificField, isSpecific);
    this.ruleScopeFromSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];
  }

  /**
   * Handles the change event of the scope to email selection field.
   * @param event
   * @private
   */
  private handleScopeToEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeToSpecificField, isSpecific);
    this.ruleScopeToSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];

    // show/hide match all recipients
    toggleVisibility(this.ruleScopeMatchAllRecipientsField, isSpecific);
  }

  /**
   * Handles the change event of the actions type field.
   * @param event
   * @private
   */
  private handleActionsTypeFieldChange(event: CustomEvent) {
    // skip if the value is the same
    if (event.detail.oldValue?.value === event.detail.newValue.value) {
      return;
    }

    // for the first change, values from backend are used for secondary actions
    if (!this.ruleActionsTypeField.hasAttribute('no-impact')) {
      this.ruleActionsDeliverToField.value = '';
      this.ruleActionsModifySubjectField.value = false;
      this.ruleActionsModifySubjectTextField.value = '';
      this.ruleActionsAppendHeaderField.value = false;
      this.ruleActionsAppendHeaderNameField.value = '';
      this.ruleActionsAppendHeaderValueField.value = '';
      this.ruleActionsSaveMailToDiskField.value = false;
      this.ruleActionsSaveMailToDiskPathField.value = '';
      this.ruleActionsArchiveToAccountField.value = false;
      this.ruleActionsArchiveToAccountAddressField.value = '';
      this.ruleActionsNotifyRecipientField.value = false;
      this.ruleActionsNotifySenderField.value = false;
      this.ruleActionsNotifyAccountField.value = false;
      this.ruleActionsNotifyAccountRecipientsField.value = '';
    } else {
      this.ruleActionsTypeField.removeAttribute('no-impact');
    }

    // show/hide deliver to field & disable it
    const toggleDeliverToVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsDeliverToField, isVisible);
      this.toggleDisabledField(this.ruleActionsDeliverToField, !isVisible);
    };
    // show/hide modify subject fields & disable it
    const toggleModifySubjectVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsModifySubjectField, isVisible);
      this.ruleActionsModifySubjectField.disabled = !isVisible;
      toggleVisibility(this.ruleActionsModifySubjectTextField, isVisible);
      this.toggleDisabledField(
        this.ruleActionsModifySubjectTextField,
        !isVisible || !this.ruleActionsModifySubjectField.value
      );
    };
    // show/hide archive to account fields & disable it
    const toggleArchiveToAccountVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsArchiveToAccountField, isVisible);
      this.ruleActionsArchiveToAccountField.disabled = !isVisible;
      toggleVisibility(this.ruleActionsArchiveToAccountAddressField, isVisible);
      this.toggleDisabledField(
        this.ruleActionsArchiveToAccountAddressField,
        !isVisible || !this.ruleActionsArchiveToAccountField.value
      );
    };
    // show/hide append header fields & disable it
    const toggleAppendHeaderFieldVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsAppendHeaderField, isVisible);
      this.ruleActionsAppendHeaderField.disabled = !isVisible;
      this.toggleActionsAppendHeaderFieldDisabledState(!isVisible || !this.ruleActionsAppendHeaderField.value);
    };
    // show notify section
    const toggleNotifySectionVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsNotifySection, isVisible);
      this.ruleActionsNotifyRecipientField.disabled = !isVisible;
      this.ruleActionsNotifySenderField.disabled = !isVisible;
      this.ruleActionsNotifyAccountField.disabled = !isVisible;
      this.toggleDisabledField(
        this.ruleActionsNotifyAccountRecipientsField,
        !isVisible || !this.ruleActionsNotifyAccountField.value
      );
    };

    switch (event.detail.newValue.value) {
      case ExchangeContentControlRuleActionType.MOVE_TO_QUARANTINE:
      case ExchangeContentControlRuleActionType.REJECT_EMAIL:
        toggleDeliverToVisibility(false);
        toggleModifySubjectVisibility(false);
        toggleArchiveToAccountVisibility(false);
        toggleAppendHeaderFieldVisibility(false);
        toggleNotifySectionVisibility(true);
        break;
      case ExchangeContentControlRuleActionType.REDIRECT:
        toggleDeliverToVisibility(true);
        toggleModifySubjectVisibility(true);
        toggleArchiveToAccountVisibility(true);
        toggleAppendHeaderFieldVisibility(true);
        toggleNotifySectionVisibility(true);
        break;
      default:
        toggleDeliverToVisibility(false);
        toggleModifySubjectVisibility(true);
        toggleArchiveToAccountVisibility(true);
        toggleAppendHeaderFieldVisibility(true);
        toggleNotifySectionVisibility(false);
        break;
    }
  }

  /**
   * Toggle disabled state of the append header fields.
   * @param disabled
   * @private
   */
  private toggleActionsAppendHeaderFieldDisabledState(disabled: boolean) {
    this.toggleDisabledField(this.ruleActionsAppendHeaderNameField, disabled);
    this.toggleDisabledField(this.ruleActionsAppendHeaderValueField, disabled);
    toggleVisibility(this.ruleActionsAppendHeaderNameField, !disabled);
    toggleVisibility(this.ruleActionsAppendHeaderValueField, !disabled);
  }

  /**
   * Toggle disabled state of the field.
   * @param field
   * @param disabled
   * @private
   */
  private toggleDisabledField(field: GzFormFieldWebComponent, disabled: boolean) {
    field.disabled = disabled;

    if (disabled) {
      field.resetField();
    } else {
      field.validate(true);
    }
  }

  /**
   * Show/hide grid, Validate settings checkboxes. At least one must be selected.
   * @private
   */
  private onSettingFilterBySubjectSelectionChanged(event: CustomEvent) {
    // show/hide grid
    toggleVisibility(this.ruleSettingsFilterBySubjectExpressionsField, event.detail.checked);
    this.ruleSettingsFilterBySubjectExpressionsField.Validators = event.detail.checked ? ruleFilteringValidators() : [];

    // validate filter by body content
    this.ruleSettingsFilterByBodyContentEnabledField?.validate(true);
  }

  /**
   * Show/hide grid, Validate settings checkboxes. At least one must be selected.
   * @private
   */
  private onSettingFilterByBodyContentSelectionChanged(event: CustomEvent) {
    // show/hide grid
    toggleVisibility(this.ruleSettingsFilterByBodyContentExpressionsField, event.detail.checked);
    this.ruleSettingsFilterByBodyContentExpressionsField.Validators = event.detail.checked
      ? ruleFilteringValidators()
      : [];

    // validate filter by subject
    this.ruleSettingsFilterBySubjectEnabledField?.validate(true);
  }
}
